import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import { v4 as uuidv4 } from "uuid";

import axios from "axios";
import { getHost } from "./utils";

axios.defaults.headers.common["AgencyID"] = window.configData.AGENCYID;
axios.defaults.headers.common["Version"] = import.meta.env.VITE_APP_VERSION;
axios.defaults.headers.common["App"] = window.configData.APP;

const TRIOS_ENABLED = false;

export const verifyFare = createAsyncThunk("flight/verifyfare", async (payload, thunkAPI) => {
  try {
    if (TRIOS_ENABLED && payload.offer.source === "trios") {
      const response = await axios.post("https://www2.tstsrv.de/ratehawkapi/api/trios/verify", {
        outboundOfferId: payload.offer.fare.outboundOfferId || payload.offer.fare.offerId,
        ...(payload.offer.fare.inboundOfferId ? { inboundOfferId: payload.offer.fare.inboundOfferId } : {}),
        priceDetailBehaviorType: "STRICT_MATCH",
      });
      console.log(response.data);
      return response.data;
    }

    const response = await axios.post("https://amadeus.holiserver.com/ratehawkapi/api/airtuerk/verify", {
      availableFareWebServiceLogIdReference: payload.offer.fare.webServiceLogId,
      fareid: payload.offer.fare.id,
      segments: payload.offer.OfferID.split("|"),
    });
    console.log(response.data);
    return response.data;
  } catch (error) {
    console.log(error);
    return thunkAPI.rejectWithValue({ error: error.message });
  }
});

// Internen Buchungsdatensatz (Agentur-eigenes System, PDF/Bestaetigung) anlegen -
// analog zum /slbBooking-Call in Booking.jsx (Paket/Unterkunft), hier fuer den
// Flug-only-Fall. Getrennt von der eigentlichen Ticketausstellung (bookFlight/
// /book gegen AirTuerk) - dort werden nur Name/Geschlecht/Geburtsdatum gebraucht,
// slbBooking haelt zusaetzlich Kontakt- und Preisdaten fuer die Agentur fest.
// Noch nirgends im UI aufgerufen (siehe FlightBooking.jsx) - bewusst inaktiv,
// bis der Ablauf (wann genau gegenueber Zahlung/Ticketausstellung) feststeht.
export const slbBookingFlight = createAsyncThunk("flight/slbbooking", async (payload, thunkAPI) => {
  try {
    const { offer, contact, travelers } = payload;
    // Gleiche Rechnung wie getOfferTotalPrice in FlightOfferCard.jsx - hier ohne
    // Import aus einer Seiten-Komponente dupliziert (Reducer haengt nicht von UI ab).
    const pricePerPerson = offer?.PriceInfo?.Price?.value ?? 0;
    const currency = offer?.PriceInfo?.Price?.CurrencyCode ?? "EUR";
    const total = pricePerPerson * (parseInt(offer?.adults) || 1);
    const response = await axios.post(getHost() + "/slbBooking", {
      bookingData: contact,
      travelers,
      theOffer: offer,
      paymentType: "viva",
      pricing: { basePrice: total, currency },
    });
    return response.data;
  } catch (error) {
    return thunkAPI.rejectWithValue({ error: error.message });
  }
});

// Verify-/Book-Antworten sind rohes SOAP->JSON vom AirTuerk-Proxy - kein
// dokumentiertes Erfolgsfeld. generalData.webServiceLogId (mit Fallbacks) ist
// das einzig verlaessliche Zeichen, dass die Antwort eine echte Nutzlast war
// und keine leere Huelle (SOAP-Fault o.ae., siehe AirtuerkRouter.js verifyCore).
export function verifyLogId(result) {
  return result?.generalData?.webServiceLogId || result?.webServiceLogId || result?.verifyAvailableFareWebServiceLogId || null;
}

// PNR aus der rohen multiBookingResponse ziehen (Struktur siehe
// AirtuerkRouter.js /book: soapenv:Envelope.soapenv:Body.(sce|ns2):multiBookingResponse
// .airBookingResponseList.bookingResponse, einzeln oder als Array).
export function extractBookingResult(raw) {
  const body = raw?.["soapenv:Envelope"]?.["soapenv:Body"];
  const bookingResponse = body?.["sce:multiBookingResponse"] ?? body?.["ns2:multiBookingResponse"];
  const single = bookingResponse?.airBookingResponseList?.bookingResponse;
  const first = Array.isArray(single) ? single[0] : single;
  return {
    pnrLocator: first?.pnr?.locator || null,
    webServiceLogId: bookingResponse?.generalData?.webServiceLogId || null,
  };
}

// ACHTUNG: instantTicketOrder ist serverseitig fest auf true gesetzt (siehe
// AirtuerkRouter.js /book) - dieser Call bucht und tickettet SOFORT und
// ENDGUELTIG. Nur nach erfolgreicher Zahlung aufrufen, nie zu Testzwecken.
export const bookFlight = createAsyncThunk("flight/bookflight", async (payload, thunkAPI) => {
  try {
    const { offer, verify, travelers } = payload;
    const response = await axios.post("https://amadeus.holiserver.com/ratehawkapi/api/airtuerk/book", {
      fareid: offer.fare.id,
      farewebservicelogid: offer.fare.webServiceLogId,
      verifyid: verifyLogId(verify),
      segments: offer.OfferID.split("|"),
      travellers: travelers.map((t) => ({
        firstName: t.firstName,
        lastName: t.lastName,
        gender: t.gender,
        ageQualifier: t.kind === "child" ? "CHILD" : "ADULT",
        ...(t.birthDate ? { dateOfBirth: t.birthDate } : {}),
      })),
    });
    return response.data;
  } catch (error) {
    return thunkAPI.rejectWithValue({ error: error.message });
  }
});

export const fetchFares = createAsyncThunk("flight/fetchfares", async (payload, thunkAPI) => {
  let state = thunkAPI.getState();

  if (!state.properties.agencyproperties || Object.keys(state.properties.agencyproperties).length === 0) {
    return thunkAPI.rejectWithValue({ error: "agencyproperties not loaded yet" });
  }

  if (state.search.stations.length < 2) {
    return thunkAPI.rejectWithValue({
      error: "departure and destination required",
    });
  }
  let segments;

  if (state.search.gabelflug) {
    segments = (state.search.gabelSegments ?? [])
      .filter((s) => s.departure?.value && s.destination?.value && s.date)
      .map((s) => ({ date: s.date, departure: s.departure.value, destination: s.destination.value }));
    if (segments.length < 2) {
      return thunkAPI.rejectWithValue({ error: "Gabelflug: mindestens 2 vollständige Segmente erforderlich" });
    }
  } else {
    segments = [
      {
        date: state.search.travelDate.from.replaceAll('"', "").split("T")[0],
        departure: state.search.stations[0].value,
        destination: state.search.stations[1].value,
      },
    ];
    if (!state.search.oneway) {
      segments.push({
        date: state.search.travelDate.until.replaceAll('"', "").split("T")[0],
        departure: state.search.stations[1].value,
        destination: state.search.stations[0].value,
      });
    }
  }
  let queryid = uuidv4();

  const providers = state.properties.agencyproperties?.ibeProviders;
  const hasInvolatus = Array.isArray(providers) && providers.some((p) => p.provider === "involatus");
  const useTrios = TRIOS_ENABLED && hasInvolatus && !state.search.gabelflug;

  if (TRIOS_ENABLED && hasInvolatus && !useTrios) {
    return thunkAPI.rejectWithValue({ error: "Trios unterstützt kein Gabelflug" });
  }

  try {
    if (useTrios) {
      const cabinTypes = [
        ...(state.search.firstclass ? ["FIRST_CLASS"] : []),
        ...(state.search.businessclass ? ["BUSINESS"] : []),
        ...(state.search.economyclass ? ["ECONOMY"] : []),
      ];
      const response = await axios.post("https://www2.tstsrv.de/ratehawkapi/api/trios", {
        origin: segments[0].departure,
        originType: "AIRPORT",
        destination: segments[0].destination,
        destinationType: "AIRPORT",
        departureDate: segments[0].date,
        ...(segments[1] ? { returnDate: segments[1].date } : {}),
        adultCount: state.search.adults.value * 1,
        ...(state.search.children.value * 1 > 0 ? { childCount: state.search.children.value * 1 } : {}),
        includeBrandDetails: true,
        includePersonPrice: true,
        ...(cabinTypes.length ? { cabinTypes } : {}),
      });

      return { data: { offers: response.data.offers, fares: [] }, queryid: queryid };
    }

    const response = await axios.post("https://amadeus.holiserver.com/ratehawkapi/api/airtuerk", {
      adults: state.search.adults.value * 1,
      children: state.search.children.value * 1 > 0 ? state.search.children.value * 1 : undefined,
      directflight: state.search.directflight,
      nofrill: state.search.nofrill,
      fsciata: state.search.fsciata,
      fscnego: state.search.fscnego,
      fscconso: state.search.fscconso,
      fsccorp: state.search.fsccorp,
      queryid: queryid,
      promosession: state.system.sessionId,
      firstclass: state.search.firstclass,
      verifiedfares: state.search.verifiedfares,
      economyclass: state.search.economyclass,
      businessclass: state.search.businessclass,
      maxstops: state.search.maxstops,
      baggageonly: state.search.baggageonly,
      segments: segments,
    });

    return { data: response.data, queryid: queryid };
  } catch (error) {
    console.log(error);
    return thunkAPI.rejectWithValue({ error: error.message });
  }
});

const initialState = {
  fares: [],
  offers: [],
  queryid: "",
  loading: false,
  // Angebot + Verify-Ergebnis, mit dem FlightBooking gestartet wurde (Klick auf
  // "Auswaehlen" -> verifyFare -> bei Erfolg hierher, siehe FlightSection.jsx).
  selectedOffer: null,
  selectedVerify: null,
  // Ergebnis von bookFlight - siehe Warnung dort (echte, sofortige Buchung).
  bookingLoading: false,
  bookingResult: null,
  bookingError: null,
  // Ergebnis von slbBookingFlight (interner Buchungsdatensatz der Agentur).
  slbBookingId: null,
  slbBookingLoading: false,
  slbBookingError: null,
};

export const flightSlice = createSlice({
  name: "flight",
  initialState,
  reducers: {
    setSelectedFlightOffer: (state, action) => {
      state.selectedOffer = action.payload.offer;
      state.selectedVerify = action.payload.verify;
    },
    clearSelectedFlightOffer: (state) => {
      state.selectedOffer = null;
      state.selectedVerify = null;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(fetchFares.pending, (state) => {
      state.loading = true;
      state.fares = [];
      state.offers = [];
    });
    builder.addCase(fetchFares.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.queryid = payload.queryid;
      state.fares = payload.data.fares;
      state.offers = payload.data.offers;
    });
    builder.addCase(fetchFares.rejected, (state) => {
      state.loading = false;
      state.fares = [];
      state.offers = [];
    });

    builder.addCase(verifyFare.pending, () => {});
    builder.addCase(verifyFare.fulfilled, (state, { payload, meta }) => {
      state.offers = state.offers.map((e) => (e.OfferID === meta.arg.offer.OfferID ? { ...e, verifiedFare: payload } : { ...e }));
    });
    builder.addCase(verifyFare.rejected, () => {});

    builder.addCase(slbBookingFlight.pending, (state) => {
      state.slbBookingLoading = true;
      state.slbBookingError = null;
    });
    builder.addCase(slbBookingFlight.fulfilled, (state, { payload }) => {
      state.slbBookingLoading = false;
      state.slbBookingId = payload?.data?.id || null;
    });
    builder.addCase(slbBookingFlight.rejected, (state, { payload }) => {
      state.slbBookingLoading = false;
      state.slbBookingError = payload?.error || "Buchung konnte nicht angelegt werden";
    });

    builder.addCase(bookFlight.pending, (state) => {
      state.bookingLoading = true;
      state.bookingError = null;
    });
    builder.addCase(bookFlight.fulfilled, (state, { payload }) => {
      state.bookingLoading = false;
      state.bookingResult = extractBookingResult(payload);
    });
    builder.addCase(bookFlight.rejected, (state, { payload }) => {
      state.bookingLoading = false;
      state.bookingError = payload?.error || "Buchung fehlgeschlagen";
    });
  },
});

export const { setSelectedFlightOffer, clearSelectedFlightOffer } = flightSlice.actions;

export default flightSlice.reducer;
